'use client';

import { useState, useRef, useEffect, useCallback, type FormEvent } from 'react';
import { useTranslations } from 'next-intl';
import { Bot, Send, Trash2, Info, Sparkles, AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import styles from './AIChat.module.css';
import dialogStyles from '@/components/ui/Dialog.module.css';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  provider?: string;
  timestamp: Date;
}

// API message format (for conversation history)
interface APIMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface AIChatProps {
  onAnalyzeUrl?: (url: string) => void;
  locale?: string; // User's preferred language ('en' or 'es')
}

// Session storage key
const SESSION_TOKEN_KEY = 'chat_session_token';

export function AIChat({ onAnalyzeUrl, locale = 'en' }: AIChatProps) {
  const t = useTranslations('aiChat');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [showClearDialog, setShowClearDialog] = useState(false);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load session token from localStorage on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const savedToken = localStorage.getItem(SESSION_TOKEN_KEY);
      if (savedToken) {
        setSessionToken(savedToken);
      }
    }
  }, []);

  // Get conversation history for API (exclude system/welcome messages)
  const getConversationHistory = useCallback((): APIMessage[] => {
    return messages
      .filter(m => m.role === 'user' || m.role === 'assistant')
      .map(m => ({ role: m.role as 'user' | 'assistant', content: m.content }));
  }, [messages]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    // Use setTimeout to ensure DOM has fully rendered
    const timer = setTimeout(() => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [messages, isLoading]);

  // Show welcome message on first load
  useEffect(() => {
    if (messages.length === 0) {
      setMessages([{
        id: 'welcome',
        role: 'system',
        content: t('welcomeMessage'),
        timestamp: new Date(),
      }]);
    }
  }, [locale, t]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const trimmedInput = input.trim();

    // Check if input is a URL using URL constructor for robust validation
    const isValidUrl = (str: string): boolean => {
      try {
        const urlToTest = str.startsWith('http') ? str : `https://${str}`;
        const url = new URL(urlToTest);
        // Ensure it has a valid hostname with at least one dot (not just localhost)
        return url.hostname.includes('.') && url.hostname.length > 3;
      } catch {
        return false;
      }
    };

    if (isValidUrl(trimmedInput) && onAnalyzeUrl) {
      // If it's a URL and we have the callback, switch to URL analysis
      const fullUrl = trimmedInput.startsWith('http') ? trimmedInput : `https://${trimmedInput}`;
      onAnalyzeUrl(fullUrl);
      setInput('');
      return;
    }

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: trimmedInput,
      timestamp: new Date(),
    };

    // Add user message
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const conversationHistory = getConversationHistory();

      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage.content,
          sessionToken: sessionToken,
          conversationHistory: conversationHistory,
          language: locale,
        }),
      });

      const data = await response.json();

      if (data.success && data.message) {
        // Save session token for future requests
        if (data.sessionToken && data.sessionToken !== sessionToken) {
          setSessionToken(data.sessionToken);
          if (typeof window !== 'undefined') {
            localStorage.setItem(SESSION_TOKEN_KEY, data.sessionToken);
          }
        }

        const assistantMessage: ChatMessage = {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: data.message,
          provider: data.provider,
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, assistantMessage]);
      } else {
        // Handle API error response (success: false)
        const errorContent = data.error || t('errorGeneric');
        const errorMessage: ChatMessage = {
          id: `error-${Date.now()}`,
          role: 'system',
          content: errorContent,
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    } catch (error) {
      // Handle network/fetch errors
      const errorMessage: ChatMessage = {
        id: `error-${Date.now()}`,
        role: 'system',
        content: t('errorConnection'),
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleClearChat = () => {
    setShowClearDialog(true);
  };

  const confirmClearChat = () => {
    // Delete the stored conversation, then forget the token. Fire-and-forget:
    // the UI resets either way and the row is unreachable without the token.
    if (sessionToken) {
      void fetch('/api/chat', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionToken }),
      }).catch(() => {});
    }
    setSessionToken(null);
    if (typeof window !== 'undefined') {
      localStorage.removeItem(SESSION_TOKEN_KEY);
    }

    setMessages([{
      id: 'welcome',
      role: 'system',
      content: t('clearedMessage'),
      timestamp: new Date(),
    }]);
    setShowClearDialog(false);
  };

  const suggestedPrompts = [
    t('suggestion1'),
    t('suggestion2'),
    t('suggestion3'),
  ];

  return (
    <div className={styles.container}>
      {/* Header with Agent Status */}
      <div className={styles.header}>
        <div className={styles.agentInfo}>
          <div className={styles.agentAvatar}>
            <Bot size={20} />
          </div>
          <div className={styles.agentDetails}>
            <h3 className={styles.agentName}>{t('agentName')}</h3>
            <p className={styles.agentStatus}>
              {isLoading ? (
                <>
                  <span className={styles.statusDot} /> {t('statusAnalyzing')}
                </>
              ) : (
                <>
                  <span className={`${styles.statusDot} ${styles.online}`} /> {t('statusReady')}
                </>
              )}
            </p>
          </div>
        </div>
        <div className={styles.headerActions}>
          <button
            type="button"
            className={styles.headerButton}
            onClick={() => setShowInfo(!showInfo)}
            aria-label={t('ariaAbout')}
          >
            <Info size={18} />
          </button>
          <button
            type="button"
            className={styles.headerButton}
            onClick={handleClearChat}
            aria-label={t('ariaClear')}
          >
            <Trash2 size={18} />
          </button>
        </div>
      </div>

      {/* Info Panel */}
      {showInfo && (
        <div className={styles.infoPanel}>
          <h4>{t('infoTitle')}</h4>
          <ul>
            <li><Sparkles size={14} /> {t('infoPrinciples')}</li>
            <li><Sparkles size={14} /> {t('infoCitations')}</li>
            <li><Sparkles size={14} /> {t('infoLaws')}</li>
            <li><Sparkles size={14} /> {t('infoAuditTab')}</li>
          </ul>
          <p className={styles.infoNote}>
            {t('infoNote')}
          </p>
        </div>
      )}

      {/* Messages Area - aria-live for screen reader announcements */}
      <div
        ref={messagesContainerRef}
        className={styles.messages}
        role="log"
        aria-live="polite"
        aria-atomic="false"
        aria-label={t('ariaMessages')}
      >
        {messages.map((message) => (
          <div
            key={message.id}
            className={`${styles.message} ${styles[message.role]}`}
          >
            {message.role !== 'user' && (
              <div className={styles.messageAvatar} aria-hidden="true">
                <Bot size={16} />
              </div>
            )}
            <div className={styles.messageContent}>
              <div className={styles.messageText}>{message.content}</div>
              {message.provider && (
                <div className={styles.messageMeta} aria-hidden="true">
                  <Sparkles size={12} />
                  <span>via interfaceaudit.com</span>
                </div>
              )}
            </div>
          </div>
        ))}

        {isLoading && (
          <div
            className={`${styles.message} ${styles.assistant}`}
            role="status"
            aria-label={t('ariaTyping')}
          >
            <div className={styles.messageAvatar} aria-hidden="true">
              <Bot size={16} />
            </div>
            <div className={styles.messageContent}>
              <div className={styles.loadingDots} aria-hidden="true">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Suggested Prompts (shown when no user messages yet) */}
      {messages.filter(m => m.role === 'user').length === 0 && !isLoading && (
        <div className={styles.suggestions}>
          <p className={styles.suggestionsLabel}>{t('suggestionsLabel')}</p>
          {suggestedPrompts.map((prompt, i) => (
            <button
              key={i}
              type="button"
              className={styles.suggestionChip}
              onClick={() => setInput(prompt)}
            >
              {prompt}
            </button>
          ))}
        </div>
      )}

      {/* Input Area */}
      <form onSubmit={handleSubmit} className={styles.inputForm}>
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={t('placeholder')}
          className={styles.input}
          disabled={isLoading}
          autoFocus
        />
        <button
          type="submit"
          className={styles.sendButton}
          disabled={!input.trim() || isLoading}
          aria-label={t('ariaSend')}
        >
          <Send size={20} />
        </button>
      </form>

      {/* Footer with transparency info */}
      <div className={styles.footer}>
        <p>
          {t('footerText')}
          {' '}
          <button
            type="button"
            className={styles.footerLink}
            onClick={() => setShowInfo(!showInfo)}
          >
            {t('learnMore')}
          </button>
        </p>
      </div>

      {/* Clear Conversation Dialog */}
      <Dialog open={showClearDialog} onOpenChange={setShowClearDialog}>
        <DialogContent>
          <DialogHeader>
            <div className={dialogStyles.headerWithIcon}>
              <div className={`${dialogStyles.iconContainer} ${dialogStyles.warning}`}>
                <AlertTriangle size={20} />
              </div>
              <DialogTitle>{t('clearTitle')}</DialogTitle>
            </div>
            <DialogDescription>
              {t('clearDescription')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setShowClearDialog(false)}
            >
              {t('clearCancel')}
            </Button>
            <Button
              variant="primary"
              onClick={confirmClearChat}
            >
              {t('clearConfirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
